import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Card,
    Checkbox,
    Col,
    Collapse,
    Divider,
    Form,
    Input,
    message,
    Modal,
    notification,
    Row,
    Spin,
    Typography,
    Upload,
} from 'antd';
import FeatherIcon from 'feather-icons-react';
import { observer } from 'mobx-react';
import { ErrorRender, FormErrorParser, Mobile, Price, RichTextEditor } from '@partsbadger/library';
import { CartItemGroup } from './CartItemGroup';
import { buildSummaryPartGroups } from './summaryGrouping';

import QuoteStore from '../../stores/QuoteStore';
import PartStore from '../../stores/PartStore';
import { hasPartsUploading } from '../../utils/uploadGate';
import { quoteRequirementsAvailable } from '../../utils/quoteRequirements';
import { Accounts, AcquisitionSource, ContactAcquisitionSource, Purchasing, Users } from '../DropDowns';
import { API_ROOT, DownloadFile, get, restricted_file_extensions, ZOHO_BASE_URL } from '@partsbadger/utils';
import { QuestionMark } from '../QuestionMark';
import { ContactDocumentsProps } from 'quotes-frontend/utils/types';
import { LeadStore } from '../../stores/LeadStore';
import { Link, useHistory } from 'react-router-dom';
import _ from 'lodash';

import { ArrowLeftOutlined, CheckCircleOutlined, EyeOutlined, LoadingOutlined } from '@ant-design/icons';
import { NoteTypeSelect } from './NoteTypeSelect';
import AdditionalFilesTypeSelect from './AdditionalFilesTypeSelect';
import { RootQuoteAdditionalFilesCard } from './RootQuoteAdditionalFilesCard';
import QuoteRequirementsEditor from '../QuoteRequirements/QuoteRequirementsEditor';
import RequirementsApplyBar from '../QuoteRequirements/RequirementsApplyBar';
import { useQuoteRequirementsDraft } from '../QuoteRequirements/useQuoteRequirementsDraft';

const { Text } = Typography;

interface Props {
    onQuoteSubmitted: (data: {
        user: {
            id: string;
            email: string;
        };
        first_name: string;
        last_name: string;
        email: string;
        company: string;
    }) => void;
    onRequestOrder?: (quote_id: string, contact_id: number) => Promise<void>;
    isReceiveSms?: boolean;
    for_staff?: boolean;
    is_tester: boolean | undefined;
    setViewMode?: (value: 'items') => void;
    /** Return to parts list and scroll to the MasterProduct card for this master_product id (groups variations). */
    onEditPartSpecifications?: (masterProductId: number, lineItemId: number) => void;
}

export const CheckoutForm = observer((props: Props) => {
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const [formErrors, setFormErrors] = useState<any | null>(null);
    const [selectQuoteAction, setSelectQuoteAction] = useState<string | null>(null);
    const [requestModalLoggedIn, setRequestModalLoggedIn] = useState<boolean>(false);

    const { onQuoteSubmitted } = props;

    const [form] = Form.useForm();

    const { getFieldValue, setFieldValue } = form;

    const { line_items } = QuoteStore.quote || {};

    const [quote_completed, setQuoteCompleted] = useState(false);
    const [quote_name, setQuoteName] = useState('');
    const [quote_id, setQuoteID] = useState<number>(0);
    const [selectAllQuoteActionsLoading, setSelectAllQuoteActionsLoading] = useState(false);

    const {
        getFirstName,
        getLastName,
        getEmail,
        getPhone,
        getCompany,
        getZip,
        getTitle,
        for_staff,
        getRelatedContact,
        getAdditionalFiles,
        contact,
    } = QuoteStore;

    const quote = QuoteStore.quote;

    const requirementsDraft = useQuoteRequirementsDraft({
        form,
        onApply: payload => handleUpdateQuoteFields(payload, false),
    });

    const submitSourceRef = useRef<string | null>(null);

    // If there are error fields in the line items, expand every incomplete
    // part and scroll to the first error on form submit.
    useEffect(() => {
        if (QuoteStore.errorFields?.length > 0) {
            QuoteStore.expandLineItemsWithErrors();
            const firstErrorLineItemId = QuoteStore.errorFields[0]?.lineItem;
            document.getElementById(`otf-line-item-${firstErrorLineItemId}`)?.scrollIntoView(false);
            setLoading(false);
        }
    }, [loading]);

    const showDelivery = !quote?.contact?.is_hobbyist || QuoteStore.isRequote;

    const summaryColSize = !showDelivery && !QuoteStore.for_staff ? 24 : 17;

    const handleSubmitRFQ = async (goToReview = false) => {
        if (hasPartsUploading(PartStore.fileParts, PartStore.pendingUploadCountFor(QuoteStore.quote_uid))) {
            // The backend only sees the parts already created: sending now would leave the rest out.
            message.warning('Some parts are still being added to this quote. Send it when they finish.');
            return;
        }
        form.validateFields()
            .then(async values => {
                const quoteAccount = quote?.account_related;
                if (QuoteStore.for_staff && quoteAccount?.id && !values.account?.key) {
                    values = {
                        ...values,
                        account: {
                            key: quoteAccount.id,
                            label: quoteAccount.name,
                        },
                    };
                }

                // setLoading(true);
                if (QuoteStore.errorFields?.length === 0) {
                    if (!quote?.line_items?.length) {
                        message.error('Please select your parts files');
                        return null;
                    }
                    let file_2d_required = '';
                    for (const item of line_items || []) {
                        if (item?.product?.material.includes('Customer')) {
                            values.customer_supplied_material = true;
                        }

                        if (item?.product?.material.includes('Review 2D') && !item?.product?.has_file_2d) {
                            file_2d_required = item.name;
                        }

                        item?.product?.finishes.forEach(finish => {
                            if (finish === 'Review 2D' && !item?.product?.has_file_2d) {
                                file_2d_required = item.name;
                            }
                        });

                        item?.product?.coatings.forEach(coating => {
                            if (coating === 'Review 2D' && !item?.product?.has_file_2d) {
                                file_2d_required = item.name;
                            }
                        });

                        if (!QuoteStore.for_staff && quote?.contact?.is_hobbyist && !item?.product?.has_file_3d) {
                            setLoading(false);
                            notification.error({
                                key: 'form-errors',
                                duration: 8,
                                message: 'Missing Required 3D File',
                                description: `A 3D file is required for '${item.name}'. Please upload the necessary file to continue.`,
                                placement: 'top',
                                maxCount: 1,
                            });
                            return;
                        }
                    }

                    if (LeadStore.IsLead) {
                        const convertLeadResponse = await convertLead();
                        // Verify the status the convertLeadToContact.

                        if (convertLeadResponse.status != 200 && convertLeadResponse.status != 202) {
                            // No Execute QuoteStore.sendToReview and end the process
                            setLoading(false);
                            return;
                        }
                    }

                    const utmParams = JSON.parse(localStorage.getItem('utm_params') || '{}');

                    const payload = {
                        first_name: values.first_name,
                        last_name: values.last_name,
                        email: values.email,
                        phone: getPhone,
                        company: values.company ? values.company : getCompany,
                        other_notes: values.other_notes,
                        customer_statement: values.customer_statement,
                        requote_changes: values.requote_changes,
                        shipping_zip_code: values.zip_code,
                        customer_rfq_reference: values.customer_rfq_reference,
                        acquisition_source: values.acquisition_source || 'Portal',
                        source: QuoteStore.for_staff ? 'One True Form - Staff' : 'One True Form - Customer',

                        create_3d_files: values.create_3d_files || false,
                        create_2d_files: values.create_2d_files || false,
                        additional_files: values.additional_files?.fileList?.map(
                            (f: any) => f.response?.id ?? Number(f.uid)
                        ),
                        is_a_referral: values.is_a_referral,
                        domestic_quoting: values.domestic_quoting,
                        customer_supplied_material: values.customer_supplied_material,

                        purchasing_type: values.purchasing_type,
                        related_contact: values.related_contact,
                        title: getTitle,

                        campaign_tracking: utmParams,
                        only_complete: goToReview,
                        note_type: values.note_type,
                    };

                    if (values.account?.key) {
                        payload['account'] = values.account.key;
                        payload['company'] = values.account.label;
                    }

                    if (values?.owner?.key) {
                        payload['owner'] = values.owner.key;
                    }

                    if (values.additional_requirements) {
                        payload['custom_requirements'] = QuoteStore.quote?.custom_requirements;
                    }

                    QuoteStore.sendToReview(payload)
                        .then(data => {
                            setLoading(false);
                            LeadStore.clearLead();
                            if (data.quote_stage === 'Completed') {
                                QuoteStore.setQuote(null);
                                setQuoteCompleted(true);
                                setQuoteID(data.quote_id);
                                setQuoteName(data.quote_name);

                                if (QuoteStore.for_staff) {
                                    history.push(`/quotes/${data.quote_id}/details/`);
                                    return;
                                }
                            } else {
                                QuoteStore.setQuote(null);

                                if (!QuoteStore.for_staff && data?.sales_order && !goToReview) {
                                    history.push(`/my-account/orders/checkout/${data.sales_order}/`);
                                    return;
                                }

                                if (QuoteStore.for_staff && data?.sales_order === null) {
                                    history.push(`/rfq/`);
                                }

                                const date = new Date();

                                // Push google tracking event to window.dataLayer
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore
                                window?.dataLayer?.push({
                                    event: 'submitted-rfq',
                                    url: window.location.toString(),
                                    time: date.getTime(),
                                });
                            }

                            if (onQuoteSubmitted) {
                                onQuoteSubmitted({
                                    user: data.user,
                                    first_name: values.first_name,
                                    last_name: values.last_name,
                                    email: values.email,
                                    company: values.company,
                                    ...data,
                                });
                                QuoteStore.setQuote(null);
                            }
                        })
                        .catch(err => {
                            message.error('Error');
                            setLoading(false);
                        });
                }
            })
            .catch(errorInfo => {
                setFormErrors(errorInfo.errorFields);
                notification.error({
                    key: 'form-errors',
                    duration: 8,
                    message: 'Please input all required fields',
                    description: <FormErrorParser isAntd4 errors={errorInfo.errorFields} />,
                    placement: 'top',
                    maxCount: 1,
                });
            });
    };

    const downloadQuote = (quote_id: number, quote_name: string) => {
        get(`/staff/quotes/${quote_id}/export/`, { responseType: 'blob' }).then(async (response: any) => {
            await DownloadFile(response, `quote-${quote_name}.pdf`);
            notification.success({ message: 'Quote PDF Downloaded' });
        });
    };

    const user = QuoteStore.current_user;

    const popup = (url: string) => {
        window.open(url, '_blank', 'width=800,height=800,left=200,top=200');
    };

    const updateAccount = async (payload = {}): Promise<void> => {
        // This is only for the staff side

        let account_id: number | null = null;
        const account = getFieldValue('account');

        if (quote && quote.account_related) {
            account_id = quote.account_related.id;
        } else if (account) {
            account_id = account.value;
        }

        if (account_id) {
            await QuoteStore.update_account_field(account_id, payload);
        } else {
            message.error('First select an account');
        }
    };

    const convertLead = async () => {
        if (LeadStore.IsLead) {
            const payload: {
                lead_zoho_id: string | null;
                account_id: string | null;
                account_zoho_id: string | null;
                title: string | null;
                acquisition_source: string | null;
                phone: string | null;
                referral: boolean;
                referral_name: string | null;
            } = {
                lead_zoho_id: LeadStore.lead?.lead_zoho_id ?? null,
                account_id: form.getFieldValue('account')?.key ?? quote?.account_related?.id ?? null,
                account_zoho_id: null,
                title: getTitle || null,
                acquisition_source: form.getFieldValue('acquisition_source_contact') ?? null,
                phone: getPhone ?? '',
                referral: form.getFieldValue('is_a_referral') ?? false,
                referral_name: form.getFieldValue('referral_name') ?? null,
            };

            setLoading(true);
            try {
                const response = await LeadStore.convertLeadToContact(payload);

                notification.success({
                    message: 'Info!',
                    description: response.message,
                    placement: 'top',
                });
                return response;
            } catch (error: any) {
                notification.error({
                    message: 'Conversion to Lead Failed. Internal Error: ' + error.response.message,
                    placement: 'topRight',
                });
                return error.response;
            } finally {
                setLoading(false);
            }
        }
    };

    const [hasAdditionalFiles, setHasAdditionalFiles] = useState((QuoteStore.quote?.additional_files?.length ?? 0) > 0);

    useEffect(() => {
        QuoteStore.getSessionUser();
    }, []);

    useEffect(() => {
        if (LeadStore.account?.id) {
            form.setFieldsValue({
                account: {
                    key: LeadStore.account?.id,
                    label: LeadStore.account?.name,
                },
            });
            handleUpdateQuoteField('account', LeadStore.account?.id);
        }
    }, [LeadStore.account?.id]);

    useEffect(() => {
        form.setFieldsValue({
            potential_dollars: quote?.account_related?.potential_dollars,
            high_or_potential_high_value_40k: quote?.account_related?.high_or_potential_high_value_40k,
            research_notes: quote?.account_related?.research_notes,
            pb_vendor_number: quote?.account_related?.pb_vendor_number,
            special_requirements: quote?.account_related?.special_requirements,
            quoting_notes: quote?.account_related?.quoting_notes,
            account: quote?.account,
        });
    }, [quote?.account_related]);

    useEffect(() => {
        if (!quote) return;
        form.setFieldsValue({
            customer_supplied_material: !!quote.customer_supplied_material,
            domestic_quoting: !!quote.domestic_quoting,
            create_2d_files: !!quote.create_2d_files,
            create_3d_files: !!quote.create_3d_files,
        });
    }, [quote?.customer_supplied_material, quote?.domestic_quoting, quote?.create_2d_files, quote?.create_3d_files]);

    useEffect(() => {
        if (quote?.custom_requirements !== undefined) {
            form.setFieldsValue({ additional_requirements: quote.custom_requirements ?? [] });
        }
    }, [quote?.custom_requirements]);

    const hasDataInAccount = (): boolean => {
        // Check if one of the 6 special fields of the account has data.
        return (
            form.getFieldValue('potential_dollars') ||
            form.getFieldValue('high_or_potential_high_value_40k') ||
            form.getFieldValue('research_notes') ||
            form.getFieldValue('pb_vendor_number') ||
            form.getFieldValue('special_requirements') ||
            form.getFieldValue('quoting_notes')
        );
    };

    const debouncedSaveCustomerRFReference = useCallback(
        _.debounce(html => {
            handleUpdateQuoteField('customer_rfq_reference', html);
        }, 500),
        []
    );

    const debouncedSaveCustomerStatement = useCallback(
        _.debounce(html => {
            handleUpdateQuoteField('customer_statement', html);
        }, 1000),
        []
    );

    const grandTotalQuote = useMemo(() => {
        const items = QuoteStore.quote?.line_items || [];
        const total = items.reduce((acc, item) => {
            // Require safe_auto_quote so a stale buy_now item cannot add an
            // instant price to the checkout total after brep-explorer failed
            // (PTB-6278).
            if (item.safe_auto_quote && !item.needs_manual_quote && item.quote_action === 'buy_now') {
                return acc + (typeof item.total_after_discount === 'number' ? item.total_after_discount : 0);
            }
            return acc;
        }, 0);
        return total;
    }, [QuoteStore.quote?.line_items]);

    const handleSelectBuyNowOrManualQuote = async (e: any, quoteAction: string) => {
        if (e.target.checked) {
            setSelectAllQuoteActionsLoading(true);
            const payload = QuoteStore.quote?.line_items
                ?.filter(item => item.safe_auto_quote)
                ?.map(item => {
                    return {
                        id: item.id,
                        quote_action: quoteAction,
                    };
                });

            await QuoteStore.updateMultipleLineItems(payload)
                .then(() => {
                    setSelectAllQuoteActionsLoading(false);
                    setSelectQuoteAction(quoteAction);
                })
                .catch((err: any) => {
                    setSelectAllQuoteActionsLoading(false);
                    notification.error({
                        message: <ErrorRender error={err} />,
                        placement: 'topRight',
                    });
                });
        } else {
            setSelectQuoteAction(null);
        }
    };

    const getQuoteActionTotals = useMemo(() => {
        const items = QuoteStore.quote?.line_items || [];
        return items.reduce(
            (acc, item) => {
                if (item.quote_action === 'buy_now') {
                    acc.buy_now += 1;
                } else if (item.quote_action === 'manual_quote') {
                    acc.manual_quote += 1;
                }
                return acc;
            },
            { buy_now: 0, manual_quote: 0 }
        );
    }, [QuoteStore.quote?.line_items]);

    const hasItemsWithBuyNow = () => {
        return QuoteStore.quote?.line_items?.some(item => item.quote_action === 'buy_now') || false;
    };

    const anyLineItemIsAutoSafeQuote = useMemo(() => {
        return QuoteStore.quote?.line_items?.some(item => item.safe_auto_quote) || false;
    }, [QuoteStore.quote?.line_items]);

    const sortedLineItems = useMemo(() => {
        const lineItems = QuoteStore.quote?.line_items || [];
        const masterProductGroups = _.map(
            _.groupBy(lineItems, item => item.product.master_product),
            (items, key) => ({
                key,
                items,
            })
        );
        // Coerce up front: Number.isFinite rejects numeric strings like "1",
        // which would silently fall back to master_product ordering.
        // Accept 0: Sort Products historically wrote 0-based positions (PTB-6840).
        // Null/undefined still fall back (Number(null)===0 must not count).
        const rawPositions = masterProductGroups.map(({ items }) => items[0].position);
        const positions = rawPositions.map(raw => Number(raw));
        const hasReliablePositions =
            rawPositions.every(raw => raw != null) &&
            positions.every(position => Number.isFinite(position) && position >= 0) &&
            new Set(positions).size === positions.length;

        return _.flatMap(
            _.orderBy(
                masterProductGroups,
                hasReliablePositions
                    ? [({ items }) => Number(items[0].position), ({ key }) => Number(key)]
                    : [({ key }) => Number(key)],
                hasReliablePositions ? ['asc', 'asc'] : ['asc']
            ),
            ({ items }) => items
        );
    }, [QuoteStore.quote?.line_items]);

    // Mirrors the Parts & Specifications grouping (master product -> variant ->
    // quantities) so the summary reads the same way: one card per part, one
    // sub-card per variant, and every requested quantity listed inside it.
    const summaryPartGroups = useMemo(() => buildSummaryPartGroups(sortedLineItems), [sortedLineItems]);

    const handleUpdateQuoteField = async (field: string, value: any, withLoading = true) => {
        setFormErrors(null);

        if (withLoading) {
            setLoading(true);
        }

        try {
            await QuoteStore.update_quote_field(field, value);
        } finally {
            if (withLoading) {
                setLoading(false);
            }
        }
    };

    const handleUpdateQuoteFields = async (payload: Record<string, unknown>, withLoading = true) => {
        setFormErrors(null);

        if (withLoading) {
            setLoading(true);
        }

        try {
            await QuoteStore.update_quote_fields(payload);
        } finally {
            if (withLoading) {
                setLoading(false);
            }
        }
    };

    const handleRFQ = () => {
        if (user?.id) {
            // if user is logged in, continue to submit the RFQ
            return handleSubmitRFQ(false);
        }

        if (hasItemsWithBuyNow()) {
            // if there are items with buy now, show the login modal first
            return setRequestModalLoggedIn(true);
        }

        // if user is not logged in and there are no items with buy now, continue to submit the RFQ
        return handleSubmitRFQ(true);
    };

    const checkAdditionalRequirements = (): boolean => {
        const requirements = QuoteStore.quote?.custom_requirements;

        if (!Array.isArray(requirements)) return false;

        return requirements.some(req => {
            const label = req?.label?.toLowerCase().trim();

            return (
                label === 'first article inspection' ||
                label === 'first article inspection (use customer form)' ||
                label == 'first article inspection (standard)'
            );
        });
    };

    return (
        <Spin spinning={loading ?? QuoteStore.loading} indicator={<LoadingOutlined />}>
            <a
                onClick={() => props.setViewMode && props.setViewMode('items')}
                className="d-lg-block inline-flex items-center gap-1 cursor-pointer"
            >
                <ArrowLeftOutlined style={{ fontSize: '24px', lineHeight: '1' }} />
                <span
                    className="align-middle mx-2"
                    style={{
                        fontSize: '16px',
                        fontWeight: 500,
                    }}
                >
                    Go Back
                </span>
            </a>

            <Form
                form={form}
                colon={false}
                onFinish={() => {
                    if (submitSourceRef.current === 'checkout') {
                        handleRFQ();
                    } else if (submitSourceRef.current === 'review') {
                        handleSubmitRFQ(true); // or other logic
                    }
                    // Reset after submit
                    submitSourceRef.current = null;
                }}
                onFinishFailed={errorInfo => {
                    setFormErrors(errorInfo.errorFields);
                }}
                layout="vertical"
                scrollToFirstError={true}
                className={'checkout'}
            >
                {/*Zoho Adds */}
                <input type="hidden" id="zc_gad" name="zc_gad" value="" />

                <Row gutter={[24, 0]}>
                    {showDelivery ? (
                        <Col md={7} span={24}>
                            <div className="otf-delivery-requirements-column">
                                <h2 className="my-4 font-bold" style={{ fontSize: '1.5rem' }}>
                                    Delivery & Requirements
                                </h2>

                                <Card className="otf-delivery-requirements-card">
                                    <Row gutter={[12, 0]}>
                                        {quoteRequirementsAvailable(quote) && (
                                            <>
                                                <Col xs={24}>
                                                    <Typography.Text
                                                        type="secondary"
                                                        style={{ marginBottom: 12, display: 'block' }}
                                                    >
                                                        Some of the options below may affect the price and lead time,
                                                        and may require review by an engineer.
                                                    </Typography.Text>
                                                </Col>
                                                <Col md={24} span={24} data-testid="quote-checkout-requirements">
                                                    <QuoteRequirementsEditor
                                                        draft={requirementsDraft}
                                                        showIntro={false}
                                                    />
                                                    {requirementsDraft.changes.count ||
                                                    requirementsDraft.applying ||
                                                    requirementsDraft.uploading ||
                                                    requirementsDraft.error ? (
                                                        <RequirementsApplyBar
                                                            draft={requirementsDraft}
                                                            onApply={async () => {
                                                                if (await requirementsDraft.apply()) {
                                                                    message.success(
                                                                        'Requirements saved. Prices recalculated.'
                                                                    );
                                                                }
                                                            }}
                                                            onSecondary={requirementsDraft.discard}
                                                            secondaryLabel="Discard"
                                                        />
                                                    ) : null}
                                                </Col>

                                                <Col md={24} span={24}>
                                                    <Card
                                                        size="small"
                                                        title={
                                                            <span>
                                                                Additional Files
                                                                <QuestionMark>
                                                                    Please upload any additional files like NDA, Tax
                                                                    Exempt Form, Photos, etc.
                                                                </QuestionMark>
                                                            </span>
                                                        }
                                                        style={{ marginBottom: 16 }}
                                                    >
                                                        <Form.Item
                                                            name="additional_files"
                                                            rules={[
                                                                { required: false, message: 'Please select a value!' },
                                                            ]}
                                                            style={{ marginBottom: hasAdditionalFiles ? 16 : 0 }}
                                                        >
                                                            <Upload.Dragger
                                                                multiple
                                                                name="file"
                                                                showUploadList={true}
                                                                action={`${API_ROOT}/customer/files/`}
                                                                defaultFileList={getAdditionalFiles}
                                                                data-testid="upload-file"
                                                                beforeUpload={file => {
                                                                    const fileNameParts = file.name.split('.');
                                                                    if (fileNameParts.length < 2) {
                                                                        message.error(
                                                                            'Files without extension are not allowed'
                                                                        );
                                                                        return Upload.LIST_IGNORE;
                                                                    }

                                                                    const fileExtension = fileNameParts
                                                                        .pop()!
                                                                        .toLowerCase();
                                                                    if (
                                                                        restricted_file_extensions.includes(
                                                                            fileExtension
                                                                        )
                                                                    ) {
                                                                        message.error(
                                                                            `File type .${fileExtension} is not allowed`
                                                                        );
                                                                        return Upload.LIST_IGNORE;
                                                                    }

                                                                    return true;
                                                                }}
                                                                onChange={info => {
                                                                    const { status } = info.file;

                                                                    if (status === 'done') {
                                                                        const additional_files = info.fileList?.map(
                                                                            (f: any) => f.response?.id ?? Number(f.uid)
                                                                        );
                                                                        if (additional_files) {
                                                                            handleUpdateQuoteField(
                                                                                'additional_files',
                                                                                additional_files
                                                                            );
                                                                        }
                                                                        setHasAdditionalFiles(true);

                                                                        message.success(
                                                                            `${info.file.name} file uploaded successfully.`
                                                                        );
                                                                    } else if (status === 'error') {
                                                                        message.error(
                                                                            `${info.file.name} file upload failed.`
                                                                        );
                                                                    } else if (status === 'removed') {
                                                                        setLoading(true);
                                                                        const remainingFiles =
                                                                            info.fileList?.map(
                                                                                (f: any) =>
                                                                                    f.response?.id ?? Number(f.uid)
                                                                            ) || [];

                                                                        if (remainingFiles.length === 0) {
                                                                            setHasAdditionalFiles(false);
                                                                            form.setFieldsValue({
                                                                                additional_files_type: null,
                                                                            });
                                                                            form.setFieldsValue({
                                                                                additional_files: [],
                                                                            });
                                                                            handleUpdateQuoteField(
                                                                                'additional_files_type',
                                                                                null,
                                                                                false
                                                                            );
                                                                            handleUpdateQuoteField(
                                                                                'additional_files',
                                                                                []
                                                                            );
                                                                        } else if (remainingFiles.length > 0) {
                                                                            handleUpdateQuoteField(
                                                                                'additional_files',
                                                                                remainingFiles
                                                                            );
                                                                        }
                                                                    }
                                                                }}
                                                            >
                                                                <div className="d-flex align-items-center align-middle justify-content-center mb-2 text-lg">
                                                                    <FeatherIcon icon="upload-cloud" />
                                                                    <span className="ml-2">Click or drag files</span>
                                                                </div>
                                                                <p>
                                                                    Add any additional files like NDAs, Tax Exempt
                                                                    Forms, Photos, etc.
                                                                </p>
                                                            </Upload.Dragger>
                                                        </Form.Item>

                                                        {hasAdditionalFiles && for_staff && (
                                                            <AdditionalFilesTypeSelect
                                                                initialValue={
                                                                    QuoteStore.quote?.additional_files_type ?? null
                                                                }
                                                                required={true}
                                                                onUpdate={handleUpdateQuoteField}
                                                            />
                                                        )}
                                                    </Card>
                                                </Col>
                                            </>
                                        )}

                                        {QuoteStore.isRequote && QuoteStore.for_staff && (
                                            <Col md={24} span={24}>
                                                <RootQuoteAdditionalFilesCard
                                                    setHasAdditionalFiles={setHasAdditionalFiles}
                                                    handleUpdateQuoteField={handleUpdateQuoteField}
                                                    onAdditionalFilesEmpty={async () => {
                                                        setHasAdditionalFiles(false);
                                                        form.setFieldsValue({
                                                            additional_files_type: null,
                                                            additional_files: [],
                                                        });
                                                        await handleUpdateQuoteField(
                                                            'additional_files_type',
                                                            null,
                                                            false
                                                        );
                                                        await handleUpdateQuoteField('additional_files', []);
                                                    }}
                                                />
                                            </Col>
                                        )}

                                        {(!quote?.contact?.is_hobbyist || QuoteStore.isRequote) && (
                                            <Col md={24} span={24}>
                                                <Form.Item
                                                    name="other_notes"
                                                    initialValue={
                                                        QuoteStore.quote
                                                            ? props.for_staff
                                                                ? QuoteStore.quote.quote_other_notes
                                                                : QuoteStore.quote.customer_statement
                                                            : ''
                                                    }
                                                    label={
                                                        <span>
                                                            {props.for_staff === false
                                                                ? 'Please note why you would like to make these changes'
                                                                : 'Notes'}
                                                        </span>
                                                    }
                                                    className="mb-0"
                                                    help={
                                                        !QuoteStore.isRequote && 'Copy and paste the customer statement'
                                                    }
                                                >
                                                    <RichTextEditor
                                                        className="richtexteditor"
                                                        onChange={html => {
                                                            debouncedSaveCustomerStatement(html);

                                                            const isEmpty =
                                                                !html || html === '<p></p>' || html.trim() === '';

                                                            if (isEmpty) {
                                                                setLoading(true);
                                                                handleUpdateQuoteField('note_type', null, false); // store
                                                                form.setFieldsValue({ note_type: undefined }); // form
                                                                handleUpdateQuoteField('other_notes', null, false); // store
                                                                form.setFieldsValue({ other_notes: undefined }); // form
                                                            }
                                                        }}
                                                    />
                                                </Form.Item>
                                                {QuoteStore.quote?.customer_statement && QuoteStore.for_staff && (
                                                    <NoteTypeSelect
                                                        initialValue={quote?.note_type}
                                                        required={true}
                                                        onUpdate={handleUpdateQuoteField}
                                                    />
                                                )}
                                            </Col>
                                        )}

                                        {QuoteStore.isRequote && QuoteStore.for_staff && QuoteStore.contact && (
                                            <Col md={24}>
                                                <Form.Item
                                                    name="owner"
                                                    label="Quote Owner"
                                                    initialValue={{
                                                        key: QuoteStore.contact.owner.id,
                                                        label: QuoteStore.contact.owner.fullname,
                                                    }}
                                                    help={'If empty the account owner will be used'}
                                                >
                                                    <Users />
                                                </Form.Item>
                                            </Col>
                                        )}
                                    </Row>

                                    {!QuoteStore.isRequote && QuoteStore.for_staff ? (
                                        <Row gutter={[8, 0]}>
                                            <Col xl={12} span={24}>
                                                <Form.Item
                                                    name="account"
                                                    label="Account"
                                                    initialValue={
                                                        QuoteStore?.quote?.account_related
                                                            ? {
                                                                  key: QuoteStore?.quote?.account_related?.id,
                                                                  label: QuoteStore?.quote?.account_related?.name,
                                                              }
                                                            : null
                                                    }
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: 'Please select an account',
                                                        },
                                                    ]}
                                                    hasFeedback={true}
                                                    help={
                                                        QuoteStore?.quote?.account_related?.id ? (
                                                            <span className="text-gray-600">
                                                                This quote is linked to this account
                                                            </span>
                                                        ) : (
                                                            <div>
                                                                If you don&apos;t see the account?
                                                                <a
                                                                    target="_blank"
                                                                    href={`${ZOHO_BASE_URL}/Accounts/create`}
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    create here
                                                                </a>
                                                            </div>
                                                        )
                                                    }
                                                >
                                                    <Accounts
                                                        read_only={Boolean(QuoteStore?.quote?.account_related?.id)}
                                                        onChange={async e => {
                                                            await handleUpdateQuoteField('account', e.value);
                                                        }}
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <>
                                                <Col xl={12} span={24}>
                                                    <Form.Item
                                                        name="acquisition_source"
                                                        label="Deal Acquisition Source"
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: 'Please input an Acquisition Source',
                                                            },
                                                        ]}
                                                        initialValue={QuoteStore.quote?.acquisition_source ?? ''}
                                                    >
                                                        <AcquisitionSource
                                                            acquisition_sources={QuoteStore.acquisition_source}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                                {LeadStore.IsLead && (
                                                    <Col xl={12} span={24}>
                                                        <Form.Item
                                                            name="acquisition_source_contact"
                                                            label="Contact Acquisition Source"
                                                            rules={[
                                                                {
                                                                    required: true,
                                                                    message:
                                                                        'Please input an Contact Acquisition Source',
                                                                },
                                                            ]}
                                                            initialValue={LeadStore.lead?.acquisition_source ?? ''}
                                                        >
                                                            <ContactAcquisitionSource
                                                                acquisition_sources={
                                                                    QuoteStore.acquisition_source_contact
                                                                }
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                )}{' '}
                                                <>
                                                    <Col xl={12} span={24}>
                                                        <Form.Item
                                                            name="related_contact"
                                                            label="Related Contact"
                                                            rules={[{ type: 'email' }]}
                                                            initialValue={getRelatedContact}
                                                            hasFeedback={true}
                                                        >
                                                            <Input />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col xl={12} span={24}>
                                                        <Form.Item
                                                            name="purchasing_type"
                                                            label="Purchasing type"
                                                            rules={[
                                                                {
                                                                    required: true,
                                                                    message: 'Please select Purchasing Type',
                                                                },
                                                            ]}
                                                            initialValue={
                                                                QuoteStore.quote?.account_related?.purchasing_type ??
                                                                null
                                                            }
                                                        >
                                                            <Purchasing />
                                                        </Form.Item>
                                                    </Col>
                                                    {!QuoteStore.for_staff && (
                                                        <Col xl={24} md={24}>
                                                            <Form.Item
                                                                name="customer_rfq_reference"
                                                                label=" Customer RFQ reference"
                                                                initialValue={quote?.customer_rfq_reference}
                                                                hasFeedback
                                                            >
                                                                <RichTextEditor
                                                                    className="richtexteditor"
                                                                    initialValue={quote?.customer_rfq_reference}
                                                                    onChange={html =>
                                                                        debouncedSaveCustomerRFReference(html)
                                                                    }
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                    )}
                                                    {quote && quote.account_related && (
                                                        <Col xl={24} md={24}>
                                                            <Collapse className={'mb-4'}>
                                                                <Collapse.Panel
                                                                    header={
                                                                        <div>
                                                                            <span>Account Info</span>
                                                                            {hasDataInAccount() && (
                                                                                <span
                                                                                    className={
                                                                                        'mx-2 font-bold text-red-500'
                                                                                    }
                                                                                >
                                                                                    !
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    }
                                                                    key="1"
                                                                >
                                                                    <Row>
                                                                        <Col className={'px-4'} md={12}>
                                                                            <Form.Item
                                                                                label="Research/Onboarding Notes"
                                                                                name={'research_notes'}
                                                                                initialValue={
                                                                                    quote?.account_related
                                                                                        ?.research_notes
                                                                                }
                                                                            >
                                                                                <Input
                                                                                    onBlur={async e => {
                                                                                        await updateAccount({
                                                                                            research_notes:
                                                                                                e.target.value,
                                                                                        });
                                                                                    }}
                                                                                />
                                                                            </Form.Item>
                                                                            <Form.Item
                                                                                label="PB Vendor Number"
                                                                                name={'pb_vendor_number'}
                                                                                initialValue={
                                                                                    quote?.account_related
                                                                                        ?.pb_vendor_number
                                                                                }
                                                                            >
                                                                                <Input
                                                                                    onBlur={async e => {
                                                                                        await updateAccount({
                                                                                            pb_vendor_number:
                                                                                                e.target.value,
                                                                                        });
                                                                                    }}
                                                                                />
                                                                            </Form.Item>
                                                                            <Form.Item
                                                                                label="Special Requirements"
                                                                                name={'special_requirements'}
                                                                                initialValue={
                                                                                    quote?.account_related
                                                                                        ?.special_requirements
                                                                                }
                                                                            >
                                                                                <Input
                                                                                    onBlur={async e => {
                                                                                        await updateAccount({
                                                                                            special_requirements:
                                                                                                e.target.value,
                                                                                        });
                                                                                    }}
                                                                                />
                                                                            </Form.Item>
                                                                        </Col>
                                                                        <Col className={'px-4'} md={12}>
                                                                            <Form.Item
                                                                                label="Quoting Notes"
                                                                                name={'quoting_notes'}
                                                                                initialValue={
                                                                                    quote?.account_related
                                                                                        ?.quoting_notes
                                                                                }
                                                                            >
                                                                                <Input
                                                                                    onBlur={async e => {
                                                                                        await updateAccount({
                                                                                            quoting_notes:
                                                                                                e.target.value,
                                                                                        });
                                                                                    }}
                                                                                />
                                                                            </Form.Item>
                                                                            <Form.Item
                                                                                label="Potential Spend"
                                                                                name={'potential_dollars'}
                                                                                initialValue={
                                                                                    quote?.account_related
                                                                                        ?.potential_dollars
                                                                                }
                                                                            >
                                                                                <Input
                                                                                    onBlur={async e => {
                                                                                        await updateAccount({
                                                                                            potential_dollars:
                                                                                                e.target.value,
                                                                                        });
                                                                                    }}
                                                                                />
                                                                            </Form.Item>
                                                                            <Form.Item
                                                                                label="High or Potential High Value"
                                                                                name={
                                                                                    'high_or_potential_high_value_40k'
                                                                                }
                                                                                initialValue={
                                                                                    quote?.account_related
                                                                                        ?.high_or_potential_high_value_40k
                                                                                }
                                                                            >
                                                                                <Input
                                                                                    onBlur={async e => {
                                                                                        await updateAccount({
                                                                                            high_or_potential_high_value_40k:
                                                                                                e.target.value,
                                                                                        });
                                                                                    }}
                                                                                />
                                                                            </Form.Item>
                                                                        </Col>
                                                                    </Row>
                                                                </Collapse.Panel>
                                                            </Collapse>
                                                        </Col>
                                                    )}
                                                </>
                                                <Col md={24}>
                                                    {QuoteStore.contact && (
                                                        <Form.Item
                                                            name="owner"
                                                            label="Quote Owner"
                                                            initialValue={{
                                                                key: QuoteStore.contact.owner.id,
                                                                label: QuoteStore.contact.owner.fullname,
                                                            }}
                                                            help={'If empty the account owner will be used'}
                                                        >
                                                            <Users />
                                                        </Form.Item>
                                                    )}
                                                </Col>
                                            </>

                                            {QuoteStore.default_documents?.length > 0 && (
                                                <Col md={24} className={'py-4'}>
                                                    <Collapse accordion>
                                                        <Collapse.Panel
                                                            header="Review the uploaded documents for the contact and account."
                                                            key="1"
                                                        >
                                                            {QuoteStore.default_documents.map(
                                                                (document: ContactDocumentsProps) => {
                                                                    return (
                                                                        <>
                                                                            <a
                                                                                onClick={() => popup(document.file)}
                                                                                rel="noreferrer"
                                                                            >
                                                                                {document.filename}
                                                                            </a>
                                                                            <br />
                                                                        </>
                                                                    );
                                                                }
                                                            )}
                                                        </Collapse.Panel>
                                                    </Collapse>
                                                </Col>
                                            )}
                                        </Row>
                                    ) : null}
                                </Card>
                            </div>
                        </Col>
                    ) : (
                        <>
                            {QuoteStore.for_staff && (
                                <Col md={6} span={24}>
                                    <h2 className="my-4 font-bold" style={{ fontSize: '1.5rem' }}>
                                        Delivery & Requirements
                                    </h2>
                                    <Card>
                                        <Row gutter={[12, 0]}>
                                            {QuoteStore.needs3DFiles() ? (
                                                <Col xl={24} span={24}>
                                                    <Form.Item
                                                        className="mb-0"
                                                        name="create_3d_files"
                                                        valuePropName="checked"
                                                        initialValue={quote?.create_3d_files}
                                                        rules={[
                                                            {
                                                                validator: (_, value) =>
                                                                    value
                                                                        ? Promise.resolve()
                                                                        : Promise.reject(
                                                                              new Error(
                                                                                  "Please check 'Create 3D Files' to proceed."
                                                                              )
                                                                          ),
                                                            },
                                                        ]}
                                                    >
                                                        <Checkbox
                                                            defaultChecked={false}
                                                            onChange={e => {
                                                                handleUpdateQuoteField(
                                                                    'create_3d_files',
                                                                    e.target.checked
                                                                );
                                                            }}
                                                        >
                                                            <Text strong>Create 3D Files</Text>
                                                        </Checkbox>
                                                    </Form.Item>
                                                </Col>
                                            ) : null}
                                        </Row>
                                    </Card>
                                </Col>
                            )}
                        </>
                    )}

                    <Col md={summaryColSize} span={24}>
                        <div className="flex items-center">
                            {QuoteStore.quote && QuoteStore.quote?.all_items_safe_auto_quote ? (
                                <Badge color="#52c41a" />
                            ) : null}
                            <h2 className="my-4 font-bold" style={{ fontSize: '1.5rem' }}>
                                Quote Summary
                            </h2>
                        </div>
                        <Card className="otf-quote-summary-container" data-testid="quote-summary">
                            <div>
                                {QuoteStore.quote && QuoteStore.quote.line_items.length >= 1 && (
                                    <Row gutter={[0, 20]}>
                                        {!QuoteStore?.quote?.all_items_safe_auto_quote &&
                                            anyLineItemIsAutoSafeQuote &&
                                            !QuoteStore.for_staff && (
                                                <Col
                                                    span={24}
                                                    className="d-flex justify-content-end mb-2"
                                                    style={{ gap: 12 }}
                                                >
                                                    <Spin spinning={selectAllQuoteActionsLoading}>
                                                        <Link
                                                            to="#"
                                                            onClick={e => {
                                                                e.preventDefault(); // prevent real navigation
                                                                handleSelectBuyNowOrManualQuote(
                                                                    { target: { checked: true } },
                                                                    'buy_now'
                                                                );
                                                            }}
                                                            style={{
                                                                // textDecoration:
                                                                //     selectQuoteAction === 'buy_now' ? 'underline' : 'none',
                                                                cursor: 'pointer',
                                                                color: '#c00',
                                                            }}
                                                        >
                                                            Select All to Buy Now
                                                        </Link>
                                                        <Divider type="vertical" />
                                                        <Link
                                                            to="#"
                                                            onClick={e => {
                                                                e.preventDefault(); // prevent real navigation
                                                                handleSelectBuyNowOrManualQuote(
                                                                    { target: { checked: true } },
                                                                    'manual_quote'
                                                                );
                                                            }}
                                                            style={{
                                                                // textDecoration:
                                                                //     selectQuoteAction === 'manual_quote' ? 'underline' : 'none',
                                                                cursor: 'pointer',
                                                                color: '#c00',
                                                            }}
                                                        >
                                                            Select All to Quote Manually
                                                        </Link>
                                                    </Spin>
                                                </Col>
                                            )}

                                        {summaryPartGroups?.map((group, index) => {
                                            return (
                                                <Col xs={24} key={group.key}>
                                                    <CartItemGroup
                                                        group={group}
                                                        partNumber={index + 1}
                                                        partCount={summaryPartGroups.length}
                                                        isAutoQuote={
                                                            QuoteStore.quote?.all_items_safe_auto_quote ? true : false
                                                        }
                                                        onEditPartSpecifications={props.onEditPartSpecifications}
                                                    />
                                                </Col>
                                            );
                                        })}
                                        <Col span={24}>
                                            <p
                                                className="otf-quote-summary-certification pt-2 font-bold"
                                                style={{
                                                    textAlign: 'left',
                                                }}
                                            >
                                                Your quote will include a Material Certificate and Certificate of
                                                Conformance.
                                            </p>
                                        </Col>
                                        <Col span={24}>
                                            <Divider
                                                className="otf-quote-summary-certification-divider"
                                                type="horizontal"
                                            />
                                        </Col>
                                    </Row>
                                )}

                                {!QuoteStore.needsRFQ() && (
                                    <Row>
                                        <Col md={16}>
                                            <Text className="font-size-lg">Total</Text>
                                        </Col>
                                        <Col md={8} className="text-right">
                                            <Text strong className="font-size-lg">
                                                <Price value={grandTotalQuote ?? 0} />
                                            </Text>
                                        </Col>
                                    </Row>
                                )}

                                <>
                                    {!for_staff && QuoteStore.quote?.contact && props.isReceiveSms !== undefined && (
                                        <Mobile
                                            contact={QuoteStore.quote?.contact}
                                            onChangeSmsNotification={value => QuoteStore.updateContactInfo(value)}
                                        />
                                    )}

                                    <>
                                        <Row gutter={[8, 0]}>
                                            <Col span={24}>
                                                <Text strong className="d-block font-size-md">
                                                    {`${getFirstName} ${getLastName}`}{' '}
                                                    {quote?.contact?.is_hobbyist && !getFieldValue('account') && (
                                                        <>- {getCompany}</>
                                                    )}
                                                </Text>
                                                <Text className="d-block font-size-md">{getEmail}</Text>
                                            </Col>

                                            {!getFirstName && (
                                                <Col xl={12} span={24}>
                                                    <Form.Item
                                                        name="first_name"
                                                        label="First Name"
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: 'Please input the first name',
                                                            },
                                                        ]}
                                                        initialValue={getFirstName}
                                                        hasFeedback={true}
                                                    >
                                                        <Input />
                                                    </Form.Item>
                                                </Col>
                                            )}

                                            {!getLastName && (
                                                <Col xl={12} span={24}>
                                                    <Form.Item
                                                        name="last_name"
                                                        label="Last Name"
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: 'Please input the last name',
                                                            },
                                                        ]}
                                                        initialValue={getLastName}
                                                        hasFeedback={true}
                                                    >
                                                        <Input />
                                                    </Form.Item>
                                                </Col>
                                            )}

                                            {!quote?.contact?.is_hobbyist && !getFieldValue('account') ? (
                                                <Col md={12} span={24}>
                                                    <Form.Item
                                                        name="company"
                                                        label="Company"
                                                        hasFeedback={true}
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: 'Please input a Company',
                                                            },
                                                        ]}
                                                        initialValue={getCompany}
                                                    >
                                                        <Input />
                                                    </Form.Item>
                                                </Col>
                                            ) : null}

                                            <Col md={12} span={24}>
                                                <Form.Item
                                                    name="zip_code"
                                                    label={
                                                        <>
                                                            Zip/Postal Code{' '}
                                                            <QuestionMark>
                                                                Zip code required for estimating sales tax
                                                            </QuestionMark>
                                                        </>
                                                    }
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: 'Please insert a Zip/Postal Code',
                                                        },
                                                    ]}
                                                    initialValue={getZip}
                                                    hasFeedback={true}
                                                >
                                                    <Input min={1} />
                                                </Form.Item>
                                            </Col>
                                            {QuoteStore.isNewContact ||
                                                (LeadStore.IsLead && (
                                                    <>
                                                        <Col md={12} span={24}>
                                                            <Form.Item name="is_a_referral" valuePropName={'checked'}>
                                                                <Checkbox
                                                                    defaultChecked={false}
                                                                    onChange={e => {
                                                                        form.resetFields(['referral_name']);
                                                                        LeadStore.IsLead &&
                                                                            LeadStore.setLead({
                                                                                ...LeadStore.lead,
                                                                                referral: e.target.checked,
                                                                            });
                                                                    }}
                                                                >
                                                                    Is this a new referral from an Existing Contact or
                                                                    Lead?
                                                                </Checkbox>
                                                            </Form.Item>
                                                            <Form.Item
                                                                label="Referral Name"
                                                                name={'referral_name'}
                                                                rules={[
                                                                    {
                                                                        required: form.getFieldValue('is_a_referral'),
                                                                        message: 'Please input a referal name',
                                                                    },
                                                                ]}
                                                            >
                                                                <Input
                                                                    type="text"
                                                                    disabled={!LeadStore?.lead?.referral}
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                    </>
                                                ))}
                                        </Row>
                                        {!for_staff &&
                                            (QuoteStore?.quote?.all_items_safe_auto_quote ||
                                                (!QuoteStore?.quote?.all_items_safe_auto_quote &&
                                                    getQuoteActionTotals?.buy_now == 0)) && (
                                                <Form.Item
                                                    name="accepted_terms_conditions"
                                                    rules={[
                                                        {
                                                            validator: (_, value) => {
                                                                // Only validate if the field is visible
                                                                if (
                                                                    !QuoteStore?.quote?.all_items_safe_auto_quote &&
                                                                    getQuoteActionTotals?.buy_now !== 0
                                                                ) {
                                                                    return Promise.resolve();
                                                                }
                                                                return value
                                                                    ? Promise.resolve()
                                                                    : Promise.reject(
                                                                          new Error(
                                                                              'Please accept the terms and conditions'
                                                                          )
                                                                      );
                                                            },
                                                        },
                                                    ]}
                                                    valuePropName="checked"
                                                    initialValue={false}
                                                >
                                                    <div
                                                        className="d-flex flex-row justify-content-start align-items-center mt-4"
                                                        data-testid="checkbox-terms"
                                                    >
                                                        <Checkbox>
                                                            I have read and accept the{' '}
                                                            <a
                                                                href={'https://parts-badger.com/terms-and-conditions'}
                                                                rel="noopener noreferrer"
                                                                target="_blank"
                                                                className="font-weight-bold underline"
                                                            >
                                                                terms and conditions.
                                                            </a>
                                                        </Checkbox>
                                                    </div>
                                                </Form.Item>
                                            )}
                                    </>

                                    {!!formErrors && (
                                        <div className={'mt-5 mb-4'}>
                                            <Alert
                                                type={'error'}
                                                description={<FormErrorParser isAntd4 errors={formErrors} />}
                                            />
                                        </div>
                                    )}

                                    {QuoteStore.for_staff && quote?.all_items_safe_auto_quote && props.is_tester && (
                                        <div>
                                            <div className={'mt-5 mb-4'}>
                                                <Alert
                                                    type={'success'}
                                                    icon={<CheckCircleOutlined />}
                                                    description={
                                                        'This quote meets all specifications for Instant Quoting! You are able to download upon selecting "Complete"'
                                                    }
                                                    showIcon
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {QuoteStore.for_staff ? (
                                        <>
                                            <Button
                                                id="submit-for-engineer-review"
                                                block={true}
                                                type="primary"
                                                loading={loading ?? QuoteStore.loading}
                                                size="large"
                                                className="mb-2"
                                                htmlType="submit"
                                                onClick={() => (submitSourceRef.current = 'review')}
                                            >
                                                {QuoteStore?.quote?.all_items_safe_auto_quote
                                                    ? 'Complete Quote and Review'
                                                    : 'Send All for Manual Quoting'}
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            {QuoteStore.quote && QuoteStore?.quote?.all_items_safe_auto_quote && (
                                                <Button
                                                    id="submit-for-engineer-review"
                                                    block={true}
                                                    type="primary"
                                                    loading={loading ?? QuoteStore.loading}
                                                    size="large"
                                                    className="mb-2"
                                                    htmlType="submit"
                                                    onClick={() => (submitSourceRef.current = 'review')}
                                                >
                                                    Complete Quote and Review
                                                </Button>
                                            )}
                                            <Button
                                                id="submit-for-checkout"
                                                block={true}
                                                type="primary"
                                                loading={loading ?? QuoteStore.loading}
                                                size="large"
                                                className="mb-2"
                                                htmlType="submit"
                                                onClick={() => (submitSourceRef.current = 'checkout')}
                                            >
                                                {QuoteStore.quote && QuoteStore?.quote?.all_items_safe_auto_quote
                                                    ? 'Complete Quote and Checkout'
                                                    : getQuoteActionTotals?.buy_now > 0 &&
                                                      getQuoteActionTotals?.manual_quote > 0
                                                    ? `Buy ${getQuoteActionTotals?.buy_now} Now and Submit ${getQuoteActionTotals?.manual_quote} items for Manual Quoting`
                                                    : 'Send All for Manual Quoting'}
                                            </Button>
                                        </>
                                    )}
                                    {/*<pre>{JSON.stringify(quote?.custom_requirements, null, 3)}</pre>*/}
                                </>
                            </div>
                        </Card>
                    </Col>
                </Row>
            </Form>
            {for_staff && props.is_tester && quote_completed && (
                <Modal
                    open={true}
                    onCancel={() => {
                        QuoteStore.setQuote(null);

                        notification.success({
                            message: 'Quote sent!',
                        });

                        history.push('/rfq');
                    }}
                    closable={false}
                    okText={'Download Quote PDF'}
                    onOk={() => {
                        downloadQuote(quote_id, quote_name);
                        QuoteStore.setQuote(null);

                        notification.success({
                            message: 'Downloading file...',
                        });

                        history.push('/rfq');
                    }}
                >
                    <span>This quote is "Auto Green", and was autocompleted, you can download the PDF file.</span>
                </Modal>
            )}

            <Modal
                open={requestModalLoggedIn}
                onCancel={() => setRequestModalLoggedIn(false)}
                closable={true}
                footer={[
                    <div key="left-buttons" style={{ float: 'left' }}>
                        <Link to="/login/">
                            <Button type="primary">Login</Button>
                        </Link>
                        <Link to="/login">
                            <Button type="default" style={{ marginLeft: 8 }}>
                                Register
                            </Button>
                        </Link>
                    </div>,
                    <Button key="close" type="default" onClick={() => setRequestModalLoggedIn(false)}>
                        Close
                    </Button>,
                ]}
            >
                <p>
                    To place an order or submit this quote, please log in to your account or create a new account if you
                    don't have one yet.
                </p>
                <p>
                    Alternatively, you can simply submit this quote for review, and a PartsBadger representative will
                    contact you shortly.
                </p>
                <p>Please login or register to continue.</p>
            </Modal>
        </Spin>
    );
});
